import type { SearchResults } from '../../api/client';

export function SearchResultsDropdown({
  debouncedTerm,
  results,
  showResults,
  onFullSearch,
  onSelect,
}: {
  debouncedTerm: string;
  results?: SearchResults;
  showResults: boolean;
  onFullSearch: () => void;
  onSelect: (path: string) => void;
}) {
  const hasResults = results && (
    results.contacts.length > 0
    || results.matters.length > 0
    || (results.documents ?? []).length > 0
    || (results.semantic_results ?? []).length > 0
  );

  if (!showResults || debouncedTerm.length < 2) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: 'var(--card)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 100,
        maxHeight: 360,
        overflowY: 'auto',
        marginTop: 4,
        border: '1px solid var(--border)',
      }}
    >
      {!hasResults && (
        <div style={{ padding: '0.75rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>No results</div>
      )}

      {results && results.matters.length > 0 && (
        <div>
          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Matters
          </div>
          {results.matters.map((m) => (
            <div
              key={`m-${m.id}`}
              onClick={() => { onSelect(`/matters/${m.id}`); }}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-light)',
                fontSize: '0.8rem',
                transition: 'background 0.1s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>{m.title}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                {m.contact_name}{m.stage_name ? ` \u00B7 ${m.stage_name}` : ''}
                {m.case_number ? ` \u00B7 #${m.case_number}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {results && results.contacts.length > 0 && (
        <div>
          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Contacts
          </div>
          {results.contacts.map((c) => (
            <div
              key={`c-${c.id}`}
              onClick={() => { onSelect(`/contacts/${c.id}`); }}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-light)',
                fontSize: '0.8rem',
                transition: 'background 0.1s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                {c.first_name} {c.last_name}
              </div>
              {(c.email || c.phone) && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  {c.email}{c.phone ? ` \u00B7 ${c.phone}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {results && (results.documents ?? []).length > 0 && (
        <div>
          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Documents
          </div>
          {results.documents.map((d) => (
            <div
              key={`d-${d.id}`}
              onClick={() => { onSelect(`/matters/${d.matter_id}`); }}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-light)',
                fontSize: '0.8rem',
                transition: 'background 0.1s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                {d.original_filename}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                {d.matter_title}{d.document_type ? ` \u00B7 ${d.document_type}` : ''}
                {d.target_folder ? ` \u00B7 ${d.target_folder}` : ''}
              </div>
              {d.snippet && (
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.snippet}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {results && (results.legal_references ?? []).length > 0 && (
        <div>
          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Legal References
          </div>
          {results.legal_references.map((lr) => (
            <div
              key={`lr-${lr.id}`}
              onClick={() => { onSelect('/law-library'); }}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-light)',
                fontSize: '0.8rem',
                transition: 'background 0.1s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                {lr.title}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                {lr.category}{lr.jurisdiction !== 'ALL' ? ` \u00B7 ${lr.jurisdiction}` : ''}
                {lr.practice_area ? ` \u00B7 ${lr.practice_area}` : ''}
              </div>
              {lr.snippet && (
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {lr.snippet}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {results && (results.semantic_results ?? []).length > 0 && (
        <div>
          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            AI-Matched Content
          </div>
          {results.semantic_results.map((sr, i) => (
            <div
              key={`sr-${sr.content_type}-${sr.source_id}-${i}`}
              onClick={() => { if (sr.matter_id) { onSelect(`/matters/${sr.matter_id}`); } }}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: sr.matter_id ? 'pointer' : 'default',
                borderBottom: '1px solid var(--border-light)',
                fontSize: '0.8rem',
                transition: 'background 0.1s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{
                  fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.35rem',
                  borderRadius: 'var(--radius-sm)', background: 'var(--primary-light, #e8e0ff)',
                  color: 'var(--primary)', textTransform: 'uppercase',
                }}>
                  {sr.content_type}
                </span>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>{sr.title || `${sr.content_type} #${sr.source_id}`}</span>
                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                  {Math.round(sr.similarity * 100)}%
                </span>
              </div>
              {sr.matter_title && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{sr.matter_title}</div>
              )}
              {sr.snippet && (
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sr.snippet}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        onClick={onFullSearch}
        style={{
          padding: '0.5rem 0.75rem', borderTop: '1px solid var(--border)',
          fontSize: '0.72rem', color: 'var(--primary)', textAlign: 'center',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        Press <kbd style={{ padding: '0.05rem 0.25rem', border: '1px solid var(--border)', borderRadius: 3, fontSize: '0.68rem' }}>Enter</kbd> or click here to search notes, activity & more
      </div>
    </div>
  );
}
